/**
 * Inline suggestion status for list rows.
 * - Processing: pulsing dot + friendly stage label (no agent names)
 * - Done: outcome pill + "See what happened →" expand with pipeline breakdown
 */

import { useState } from "react";
import type { Suggestion } from "@/lib/types";

const STAGE_ORDER = ["queued", "feedback", "critic", "security", "review", "done"];

const ACTIVE_LABEL: Record<string, string> = {
  queued: "In queue…",
  feedback: "Preparing changes…",
  critic: "Quality check…",
  security: "Security scan…",
  manual_review: "Manual security review required",
  review: "Waiting for owner review",
};

const STEPS = [
  { stage: "feedback", pass: "Changes prepared",  fail: "Review failed" },
  { stage: "critic",   pass: "Quality approved",   fail: "Quality check failed" },
  { stage: "security", pass: "Security cleared",   fail: "Security check failed" },
  { stage: "review",   pass: "Owner approved",     fail: "Owner rejected" },
];

export function SuggestionStatus({ s, expandable = true }: { s: Suggestion; expandable?: boolean }) {
  const [expanded, setExpanded] = useState(false);

  const isLive   = s.status === "processing" || s.status === "pending";
  const isReady  = s.status === "ready";
  const isMerged = s.status === "merged";
  const isDone   = isMerged || s.status === "rejected";

  if (s.status === "conflict") {
    return (
      <span className="flex items-center gap-1.5 text-xs">
        <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-red-500" />
        <span className="font-medium text-red-600 dark:text-red-400">
          Conflict — needs your resolution
        </span>
      </span>
    );
  }

  if (isLive || isReady) {
    const label = isReady
      ? "Waiting for owner review"
      : (ACTIVE_LABEL[s.stage] ?? "In progress…");
    return (
      <span className="flex items-center gap-1.5 text-xs">
        <span
          className={`h-1.5 w-1.5 shrink-0 animate-pulse rounded-full ${
            isReady ? "bg-yellow-500" : "bg-cyan-400"
          }`}
        />
        <span className={isReady ? "text-yellow-700 dark:text-yellow-300" : "text-cyan-600 dark:text-cyan-400"}>
          {label}
        </span>
      </span>
    );
  }

  if (isDone) {
    const rejectIdx = s.reject_stage ? STAGE_ORDER.indexOf(s.reject_stage) : -1;
    return (
      <div className="space-y-1.5">
        <div className="flex flex-wrap items-center gap-2">
          <span
            className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${
              isMerged
                ? "bg-green-500/15 text-green-600 dark:text-green-400"
                : "bg-red-500/15 text-red-600 dark:text-red-400"
            }`}
          >
            {isMerged ? "✓ Merged" : "✗ Not merged"}
          </span>
          {expandable && (
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setExpanded((x) => !x);
              }}
              className="text-[11px] text-text/35 transition hover:text-text/60"
            >
              {expanded ? "Hide ↑" : "See what happened →"}
            </button>
          )}
        </div>

        {expanded && (
          <div className="rounded-xl border border-black/10 bg-black/[0.025] p-3 dark:border-white/10 dark:bg-white/[0.025]">
            <div className="space-y-2">
              {STEPS.map(({ stage, pass, fail }) => {
                const idx = STAGE_ORDER.indexOf(stage);
                const state =
                  isMerged
                    ? "pass"
                    : idx < rejectIdx
                    ? "pass"
                    : idx === rejectIdx
                    ? "fail"
                    : "skip";
                return (
                  <div key={stage} className="flex items-center gap-2 text-xs">
                    <span
                      className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-full text-[9px] font-bold ${
                        state === "pass"
                          ? "bg-green-500/15 text-green-600 dark:text-green-400"
                          : state === "fail"
                          ? "bg-red-500/15 text-red-600 dark:text-red-400"
                          : "bg-black/5 text-text/30 dark:bg-white/5"
                      }`}
                    >
                      {state === "pass" ? "✓" : state === "fail" ? "✕" : "—"}
                    </span>
                    <span
                      className={
                        state === "pass"
                          ? "text-text/70"
                          : state === "fail"
                          ? "text-red-600 dark:text-red-400/80"
                          : "text-text/30"
                      }
                    >
                      {state === "fail" ? fail : pass}
                    </span>
                  </div>
                );
              })}
              {isMerged && s.changelog && (
                <div className="mt-2 border-t border-black/10 pt-2 text-xs text-text/55 dark:border-white/10 dark:text-text/45">
                  <span className="text-text/30">What changed: </span>
                  {s.changelog}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }

  return <span className="text-xs italic text-text/35">{s.status}</span>;
}
